import { downloadFile } from './utils.js'
import { normalizeEstimatorInputs } from './estimator.js'
import {
  DEFAULT_AFTER_TAX,
  CASH_BALANCE_SUPPORTED_PLAN_YEAR,
  DEFAULT_NONDISCRIMINATION_TESTING,
  DEFAULT_TERMINATION_HANDLING,
  DEFAULT_VESTING,
  PLAN_LIMITS,
} from './constants.js'
import { normalizeSettingMetadata } from './settingMetadata.js'
import { normalizeNewCompProposal } from './newCompProposal.js'
import {
  createDefaultProfitSharingDesign,
  createDefaultCashBalanceDesign,
  normalizeCashBalanceDesign,
  normalizeMatchGroup,
  normalizeMatchTier,
  normalizeProfitSharingDesign,
  validateProfitSharingDesign,
} from './planDesign.js'

// v10 is the only supported portable/authored plan contract: one rate per
// allocation class (classes come from the census), one standard profit-sharing
// formula, and structured cash balance.
export const PLAN_SETTINGS_VERSION = 10
export const PLAN_SETTINGS_KEYS = ['planA', 'planB', 'planC', 'planD']

const PLAN_SETTINGS_TYPE = '401k-plan-settings'
const currentYear = new Date().getFullYear()
const defaultPlanYear = PLAN_LIMITS[currentYear] ? currentYear : 2026
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value)
const deepClone = (value) => JSON.parse(JSON.stringify(value))

export const DEFAULT_EMPLOYER_PROFILE = Object.freeze({
  eligibleEmployeeCount: null,
  controlledGroupStatus: 'unknown',
  affiliatedServiceGroupStatus: 'unknown',
  priorDbPlanHistory: 'unknown',
  source: 'manual',
})

export function normalizeEmployerProfile(value = {}) {
  const source = isObject(value) ? value : {}
  const status = (candidate) => ['yes', 'no', 'unknown'].includes(candidate) ? candidate : 'unknown'
  const count = Number(source.eligibleEmployeeCount)
  return {
    ...DEFAULT_EMPLOYER_PROFILE,
    eligibleEmployeeCount: Number.isInteger(count) && count >= 0 ? count : null,
    controlledGroupStatus: status(source.controlledGroupStatus),
    affiliatedServiceGroupStatus: status(source.affiliatedServiceGroupStatus),
    priorDbPlanHistory: status(source.priorDbPlanHistory),
    source: ['manual', 'plan-document', 'plan-json', 'client-book'].includes(source.source) ? source.source : 'manual',
  }
}

export function normalizeSettingMetadataByPlan(value = {}) {
  if (!isObject(value)) return {}
  return Object.fromEntries(PLAN_SETTINGS_KEYS.flatMap((key) => (
    isObject(value[key]) ? [[key, normalizeSettingMetadata(value[key])]] : []
  )))
}

const NONDISCRIMINATION_GATEWAY_ROUTES = ['gateway', 'broadly-available', 'age-service-schedule']

export function normalizeNondiscriminationTesting(value = {}) {
  const source = isObject(value) ? value : {}
  const qualification = isObject(source.gatewayRouteQualification)
    ? {
        confirmed: source.gatewayRouteQualification.confirmed === true,
        source: ['plan-document', 'tpa', 'actuary'].includes(source.gatewayRouteQualification.source)
          ? source.gatewayRouteQualification.source : null,
        note: typeof source.gatewayRouteQualification.note === 'string'
          ? source.gatewayRouteQualification.note : '',
      }
    : null
  return {
    otherwiseExcludable: source.otherwiseExcludable === true,
    gatewayRoute: NONDISCRIMINATION_GATEWAY_ROUTES.includes(source.gatewayRoute)
      ? source.gatewayRoute : 'gateway',
    ...(qualification ? { gatewayRouteQualification: qualification } : {}),
    imputePermittedDisparity: source.imputePermittedDisparity === true,
    rateBanding: source.rateBanding === true,
  }
}

// Match/NEC employer-contribution fields in the authored v10 contract.
const DEFAULT_MATCH_NEC = Object.freeze({
  matchEnabled: true,
  matchMode: 'simple',
  matchCapPerEmployee: 0,
  necEnabled: false,
  necPercent: 3,
  necIsSafeHarbor: true,
})

const normalizeMatchNecContributions = (value = {}) => {
  const source = isObject(value) ? value : {}
  return {
    matchEnabled: source.matchEnabled === undefined ? DEFAULT_MATCH_NEC.matchEnabled : source.matchEnabled === true,
    matchMode: source.matchMode === 'grouped' ? 'grouped' : 'simple',
    matchTiers: Array.isArray(source.matchTiers) && source.matchTiers.length
      ? source.matchTiers.map(normalizeMatchTier)
      : [{ matchPercent: 100, upToPercent: 1 }, { matchPercent: 50, upToPercent: 6 }],
    matchGroups: Array.isArray(source.matchGroups) ? source.matchGroups.map(normalizeMatchGroup) : [],
    // Numeric values pass through unclamped: an out-of-range value must
    // survive normalization so
    // validatePlan can refuse it — silent correction would defeat the
    // assistant/import refusal paths.
    matchCapPerEmployee: isFiniteNumber(source.matchCapPerEmployee)
      ? source.matchCapPerEmployee : DEFAULT_MATCH_NEC.matchCapPerEmployee,
    necEnabled: source.necEnabled === true,
    necPercent: isFiniteNumber(source.necPercent) ? source.necPercent : DEFAULT_MATCH_NEC.necPercent,
    necIsSafeHarbor: source.necIsSafeHarbor === undefined ? DEFAULT_MATCH_NEC.necIsSafeHarbor : source.necIsSafeHarbor === true,
  }
}

export function createDefaultPlan(name = 'Plan A') {
  return {
    name,
    planYear: defaultPlanYear,
    employerContributions: {
      ...normalizeMatchNecContributions(),
      profitSharing: createDefaultProfitSharingDesign(),
    },
    eligibility: {
      enabled: true,
      minAge: 21,
      minServiceMonths: 12,
      minHours: 1000,
    },
    vesting: deepClone(DEFAULT_VESTING),
    terminationHandling: deepClone(DEFAULT_TERMINATION_HANDLING),
    nondiscriminationTesting: deepClone(DEFAULT_NONDISCRIMINATION_TESTING),
    afterTax: deepClone(DEFAULT_AFTER_TAX),
    cashBalance: createDefaultCashBalanceDesign(defaultPlanYear),
    prorateCompensation: false,
  }
}

// Universal authored-plan entry point. Unsupported shapes reset to the current
// defaults, keeping one persistence and calculation path.
export function normalizePlan(plan = {}, name = 'Plan A') {
  const defaults = createDefaultPlan(name)
  const planYear = plan.planYear ?? defaults.planYear
  const eligibility = isObject(plan.eligibility) ? plan.eligibility : {}
  const cleanedEligibility = Object.fromEntries(
    Object.entries(eligibility).filter(([key, value]) => key !== 'ltptEnabled' && value !== undefined),
  )

  const vesting = {
    ...defaults.vesting,
    ...(isObject(plan.vesting) ? deepClone(plan.vesting) : {}),
  }
  delete vesting.cashBalanceSchedule

  const shared = {
    name: typeof plan.name === 'string' ? plan.name : defaults.name,
    planYear,
    eligibility: {
      ...defaults.eligibility,
      ...cleanedEligibility,
    },
    vesting,
    terminationHandling: {
      ...defaults.terminationHandling,
      ...(isObject(plan.terminationHandling) ? deepClone(plan.terminationHandling) : {}),
    },
    nondiscriminationTesting: normalizeNondiscriminationTesting(plan.nondiscriminationTesting),
    afterTax: {
      ...defaults.afterTax,
      ...(isObject(plan.afterTax) ? deepClone(plan.afterTax) : {}),
    },
    prorateCompensation: plan.prorateCompensation ?? defaults.prorateCompensation,
  }

  const contributions = isObject(plan.employerContributions) ? plan.employerContributions : {}
  const psAuthored = isObject(contributions.profitSharing)
  const cashBalance = normalizeCashBalanceDesign(plan.cashBalance, planYear)
  // Additive: the applied new-comparability proposal record (goal, selected
  // scenario, acknowledgment, provenance) travels with the plan. Absent or
  // malformed collapses to "no proposal", never to a guessed one.
  const newCompProposal = normalizeNewCompProposal(plan.newCompProposal)
  const proposalField = newCompProposal ? { newCompProposal } : {}

  const normalizedProfitSharing = psAuthored
    ? normalizeProfitSharingDesign(contributions.profitSharing)
    : createDefaultProfitSharingDesign()
  return {
    ...defaults,
    ...shared,
    employerContributions: {
      ...normalizeMatchNecContributions(contributions),
      profitSharing: normalizedProfitSharing.mode === 'standard'
        ? normalizedProfitSharing : createDefaultProfitSharingDesign(),
    },
    cashBalance,
    ...proposalField,
  }
}

// Merge a partial update into the single authored contract. Unknown keys are
// ignored by normalization and never round-trip through the calculation model.
export function mergePlan(plan, updates = {}) {
  const contributionUpdates = updates.employerContributions || {}
  const cashBalance = isObject(updates.cashBalance)
    ? { ...plan.cashBalance, ...updates.cashBalance }
    : plan.cashBalance
  const employerContributions = {
    ...plan.employerContributions,
    ...contributionUpdates,
    ...(isObject(contributionUpdates.profitSharing)
      ? {
          profitSharing: {
            ...plan.employerContributions?.profitSharing,
            ...contributionUpdates.profitSharing,
          },
        }
      : {}),
  }

  return normalizePlan({
    ...plan,
    ...updates,
    employerContributions,
    eligibility: {
      ...plan.eligibility,
      ...(updates.eligibility || {}),
    },
    vesting: {
      ...plan.vesting,
      ...(updates.vesting || {}),
    },
    terminationHandling: {
      ...plan.terminationHandling,
      ...(updates.terminationHandling || {}),
    },
    afterTax: {
      ...plan.afterTax,
      ...(updates.afterTax || {}),
    },
    cashBalance,
  }, plan.name)
}

export function copyPlanWithName(sourcePlan, name) {
  return normalizePlan({
    ...deepClone(sourcePlan),
    name,
  }, name)
}

export function arePlansEquivalent(planA, planB) {
  const normalizedA = normalizePlan(planA, 'Plan A')
  const normalizedB = normalizePlan(planB, 'Plan B')
  normalizedB.name = normalizedA.name
  return JSON.stringify(normalizedA) === JSON.stringify(normalizedB)
}

// Always returns all four plan slots (null when absent) plus the optional
// estimate block, so callers can assign the result straight into the store.
//
// `version` is the version the payload was WRITTEN at, which is not always the
// current one: client-book rows keep the version of the build that saved them
// until they are re-saved, so this has to read every shape the app has ever
// written. Defaulting to the current version keeps callers that hand over
// freshly-built settings unchanged.
export function normalizePlanSettings(settings, version = PLAN_SETTINGS_VERSION) {
  // Estimate assumptions are non-PII and normalize field-by-field. Anything
  // malformed collapses to "not an estimate" rather than throwing.
  const estimate = isObject(settings.estimate) && isObject(settings.estimate.inputs)
    ? { inputs: normalizeEstimatorInputs(settings.estimate.inputs) }
    : null

  if (version >= 6) {
    // Rows saved before v6 carry the old comparisonEnabled flag, which gated
    // whether planB was a real comparison plan or an invisible stashed copy –
    // honor an explicit false by dropping the stashed planB.
    const legacyPlanBHidden = settings.comparisonEnabled === false
    return {
      planA: normalizePlan(settings.planA, 'Plan A'),
      planB: !legacyPlanBHidden && isObject(settings.planB) ? normalizePlan(settings.planB, 'Plan B') : null,
      planC: isObject(settings.planC) ? normalizePlan(settings.planC, 'Plan C') : null,
      planD: isObject(settings.planD) ? normalizePlan(settings.planD, 'Plan D') : null,
      employerProfile: normalizeEmployerProfile(settings.employerProfile),
      settingMetadataByPath: normalizeSettingMetadataByPlan(settings.settingMetadataByPath),
      ...(estimate ? { estimate } : {}),
    }
  }

  // v3–v5 always carried a planB alongside a comparisonEnabled flag; the plan
  // only counts as a comparison plan when the flag was on. Older payloads kept
  // the alternative under comparisonPlan and the whole object WAS plan A.
  const legacyPlanB = version >= 3 ? settings.planB : (settings.comparisonPlan || {})
  const planA = version >= 3 ? normalizePlan(settings.planA, 'Plan A') : normalizePlan(settings, 'Plan A')
  return {
    planA,
    planB: settings.comparisonEnabled ? normalizePlan(legacyPlanB, 'Plan B') : null,
    planC: null,
    planD: null,
    employerProfile: normalizeEmployerProfile(settings.employerProfile),
    settingMetadataByPath: normalizeSettingMetadataByPlan(settings.settingMetadataByPath),
    ...(estimate ? { estimate } : {}),
  }
}

const assertOptionalBoolean = (object, key, context) => {
  if (object[key] !== undefined && typeof object[key] !== 'boolean') {
    throw new Error(`${context}.${key} must be true or false.`)
  }
}

const assertOptionalNonNegativeNumber = (object, key, context) => {
  if (object[key] !== undefined && (!isFiniteNumber(object[key]) || object[key] < 0)) {
    throw new Error(`${context}.${key} must be a non-negative number.`)
  }
}

const validatePlanYear = (planYear, context) => {
  if (!Number.isInteger(planYear) || !PLAN_LIMITS[planYear]) {
    throw new Error(`${context} must be one of: ${Object.keys(PLAN_LIMITS).join(', ')}.`)
  }
}

const validateSchedule = (schedule, context) => {
  if (!isObject(schedule) || !['immediate', 'cliff', 'graded'].includes(schedule.type)) {
    throw new Error(`${context} is not a valid vesting schedule.`)
  }
  if (schedule.type === 'cliff' && (!isFiniteNumber(schedule.years) || schedule.years < 0)) {
    throw new Error(`${context}.years must be a non-negative number.`)
  }
  if (schedule.type === 'graded') {
    if (!Array.isArray(schedule.schedule) || schedule.schedule.length === 0) {
      throw new Error(`${context}.schedule must contain at least one entry.`)
    }
    schedule.schedule.forEach((entry, index) => {
      if (
        !isObject(entry)
        || !isFiniteNumber(entry.years)
        || entry.years < 0
        || !isFiniteNumber(entry.percent)
        || entry.percent < 0
        || entry.percent > 100
      ) {
        throw new Error(`${context}.schedule[${index}] is invalid.`)
      }
    })
  }
}

const validateMatchTier = (tier, contextLabel) => {
  if (
    !isObject(tier)
    || !isFiniteNumber(tier.matchPercent)
    || tier.matchPercent < 0
    || !isFiniteNumber(tier.upToPercent)
    || tier.upToPercent < 0
  ) {
    throw new Error(`${contextLabel} is invalid.`)
  }
}

const validateConditions = (conditions, context) => {
  if (!isObject(conditions)) throw new Error(`${context} must be an object.`)
  if (!isFiniteNumber(conditions.minHours) || conditions.minHours < 0) {
    throw new Error(`${context}.minHours must be a non-negative number.`)
  }
  if (typeof conditions.lastDayRequired !== 'boolean') {
    throw new Error(`${context}.lastDayRequired must be true or false.`)
  }
  if (conditions.combinator !== 'and' || conditions.waivers !== null) {
    throw new Error(`${context} supports only combinator "and" with no waivers in this version.`)
  }
}

const validateEmployerContributions = (contributions, context, _plan) => {
  if (!isObject(contributions)) throw new Error(`${context} must be an object.`)
  for (const key of ['matchEnabled', 'necEnabled', 'necIsSafeHarbor']) {
    assertOptionalBoolean(contributions, key, context)
  }
  for (const key of ['matchCapPerEmployee', 'necPercent']) {
    assertOptionalNonNegativeNumber(contributions, key, context)
  }
  if (
    contributions.matchMode !== undefined
    && !['simple', 'grouped'].includes(contributions.matchMode)
  ) {
    throw new Error(`${context}.matchMode must be "simple" or "grouped".`)
  }
  if (!Array.isArray(contributions.matchTiers) || contributions.matchTiers.length === 0) {
    throw new Error(`${context}.matchTiers must contain at least one tier.`)
  }
  contributions.matchTiers.forEach((tier, index) => {
    validateMatchTier(tier, `${context}.matchTiers[${index}]`)
  })
  if (!Array.isArray(contributions.matchGroups)) {
    throw new Error(`${context}.matchGroups must be an array.`)
  }
  contributions.matchGroups.forEach((entry, index) => {
    if (!isObject(entry)) throw new Error(`${context}.matchGroups[${index}] is invalid.`)
    if (typeof entry.groupId !== 'string' || !entry.groupId) {
      throw new Error(`${context}.matchGroups[${index}].groupId must reference an allocation group.`)
    }
    if (!Array.isArray(entry.matchTiers) || entry.matchTiers.length === 0) {
      throw new Error(`${context}.matchGroups[${index}].matchTiers must contain at least one tier.`)
    }
    entry.matchTiers.forEach((tier, tierIndex) => {
      validateMatchTier(tier, `${context}.matchGroups[${index}].matchTiers[${tierIndex}]`)
    })
  })
  // Classes come from the census, so a rate pointing at a class the loaded
  // census does not carry is a review finding, not an invalid plan. Only the
  // one-rate-per-class rule is structural.
  const matchGroupIds = contributions.matchGroups.map((entry) => entry.groupId)
  if (new Set(matchGroupIds).size !== matchGroupIds.length) {
    throw new Error(`${context}.matchGroups may define each allocation class only once.`)
  }
  const design = contributions.profitSharing
  if (!isObject(design) || design.mode !== 'standard') {
    throw new Error(`${context}.profitSharing must be a standard authored design.`)
  }
  assertOptionalBoolean(design, 'enabled', `${context}.profitSharing`)
  validateConditions(design.conditions, `${context}.profitSharing.conditions`)
  const errors = validateProfitSharingDesign(design)
  if (errors.length) {
    throw new Error(`${context}.${errors[0].path}: ${errors[0].message}`)
  }
}

const validateCashBalance = (cashBalance, context, planYear) => {
  if (!isObject(cashBalance) || cashBalance.mode !== 'structured') {
    throw new Error(`${context} must be a structured cash-balance design.`)
  }
  assertOptionalBoolean(cashBalance, 'enabled', context)
  if (cashBalance.enabled && Number(planYear) !== CASH_BALANCE_SUPPORTED_PLAN_YEAR) {
    throw new Error(`${context}.enabled is supported only for the ${CASH_BALANCE_SUPPORTED_PLAN_YEAR} plan year.`)
  }
  if (!Array.isArray(cashBalance.credits)) throw new Error(`${context}.credits must be an array.`)
  const creditGroupIds = cashBalance.credits.map((credit) => credit?.groupId)
  if (new Set(creditGroupIds).size !== creditGroupIds.length) {
    throw new Error(`${context}.credits may define each allocation group only once.`)
  }
  cashBalance.credits.forEach((credit, index) => {
    const label = `${context}.credits[${index}]`
    if (!isObject(credit) || !credit.groupId || !isObject(credit.formula)) throw new Error(`${label} is invalid.`)
    if (!['percentOfPay', 'flatDollar', 'serviceGraded', 'permittedDisparity'].includes(credit.formula.method)) {
      throw new Error(`${label}.formula.method is invalid.`)
    }
    if (['percentOfPay', 'flatDollar'].includes(credit.formula.method)) {
      assertOptionalNonNegativeNumber(credit.formula, 'value', `${label}.formula`)
    } else if (credit.formula.method === 'serviceGraded') {
      if (!Array.isArray(credit.formula.bands) || credit.formula.bands.length === 0) {
        throw new Error(`${label}.formula.bands must contain at least one service band.`)
      }
    } else {
      assertOptionalNonNegativeNumber(credit.formula, 'basePercent', `${label}.formula`)
      assertOptionalNonNegativeNumber(credit.formula, 'excessPercent', `${label}.formula`)
    }
  })
  if (cashBalance.interestCredit?.type === 'fixed') {
    if (!isFiniteNumber(cashBalance.interestCredit.rate)
      || cashBalance.interestCredit.rate < 0 || cashBalance.interestCredit.rate > 6) {
      throw new Error(`${context}.interestCredit fixed rate must be between 0 and 6.`)
    }
  } else if (cashBalance.interestCredit?.type === 'index') {
    for (const key of ['floor', 'cap', 'assumptionRate']) {
      if (!isFiniteNumber(cashBalance.interestCredit[key])) {
        throw new Error(`${context}.interestCredit.${key} must be a number for an indexed credit.`)
      }
    }
    if (cashBalance.interestCredit.floor > cashBalance.interestCredit.cap) {
      throw new Error(`${context}.interestCredit floor cannot exceed its cap.`)
    }
  } else {
    throw new Error(`${context}.interestCredit.type must be fixed or index.`)
  }
  if (!isObject(cashBalance.eligibility)) {
    throw new Error(`${context}.eligibility must be an object.`)
  }
  for (const key of ['minAge', 'minServiceMonths', 'minHours']) {
    assertOptionalNonNegativeNumber(cashBalance.eligibility, key, `${context}.eligibility`)
  }
}

export const validatePlan = (plan, context) => {
  if (!isObject(plan)) throw new Error(`${context} must be an object.`)
  if (typeof plan.name !== 'string') throw new Error(`${context}.name must be text.`)
  validatePlanYear(plan.planYear, `${context}.planYear`)
  validateEmployerContributions(plan.employerContributions, `${context}.employerContributions`, plan)

  if (!isObject(plan.eligibility)) throw new Error(`${context}.eligibility must be an object.`)
  assertOptionalBoolean(plan.eligibility, 'enabled', `${context}.eligibility`)
  for (const key of ['minAge', 'minServiceMonths', 'minHours']) {
    assertOptionalNonNegativeNumber(plan.eligibility, key, `${context}.eligibility`)
  }

  if (!isObject(plan.vesting)) throw new Error(`${context}.vesting must be an object.`)
  assertOptionalBoolean(plan.vesting, 'enabled', `${context}.vesting`)
  for (const key of ['matchSchedule', 'necSchedule', 'profitSharingSchedule']) {
    validateSchedule(plan.vesting[key], `${context}.vesting.${key}`)
  }

  if (!isObject(plan.terminationHandling)) {
    throw new Error(`${context}.terminationHandling must be an object.`)
  }
  assertOptionalBoolean(plan.terminationHandling, 'includeInMetrics', `${context}.terminationHandling`)
  assertOptionalBoolean(
    plan.terminationHandling,
    'terminatedEarnContributions',
    `${context}.terminationHandling`,
  )
  assertOptionalNonNegativeNumber(
    plan.terminationHandling,
    'shortTermThresholdDays',
    `${context}.terminationHandling`,
  )

  if (plan.afterTax !== undefined) {
    if (!isObject(plan.afterTax)) throw new Error(`${context}.afterTax must be an object.`)
    assertOptionalBoolean(plan.afterTax, 'allowed', `${context}.afterTax`)
    for (const key of ['maxPercent', 'maxDollar']) {
      assertOptionalNonNegativeNumber(plan.afterTax, key, `${context}.afterTax`)
    }
  }

  validateCashBalance(plan.cashBalance, `${context}.cashBalance`, plan.planYear)

  assertOptionalBoolean(plan, 'prorateCompensation', context)

  if (plan.newCompProposal !== undefined && plan.newCompProposal !== null && !isObject(plan.newCompProposal)) {
    throw new Error(`${context}.newCompProposal must be an object when present.`)
  }
}

export function pickPlanSettings(state) {
  return {
    planA: state.planA,
    ...(state.planB ? { planB: state.planB } : {}),
    ...(state.planC ? { planC: state.planC } : {}),
    ...(state.planD ? { planD: state.planD } : {}),
    // An Estimate Mode session saves its assumptions (aggregates + seed – no
    // census rows), so loading it regenerates the same synthetic census.
    ...(state.workspaceMode === 'estimate' && state.estimator?.inputs
      ? { estimate: { inputs: state.estimator.inputs } }
      : {}),
    employerProfile: normalizeEmployerProfile(state.employerProfile),
    settingMetadataByPath: normalizeSettingMetadataByPlan(state.settingMetadataByPath),
  }
}

export function buildPlanSettingsPayload(state) {
  return {
    _type: PLAN_SETTINGS_TYPE,
    _version: PLAN_SETTINGS_VERSION,
    _exportedAt: new Date().toISOString(),
    settings: pickPlanSettings(state),
  }
}

export function exportPlanSettings(state) {
  const json = JSON.stringify(buildPlanSettingsPayload(state), null, 2)
  downloadFile(json, '401k-plan-settings.json', 'application/json')
}

export function parsePlanSettingsJson(json) {
  let parsed
  try {
    parsed = JSON.parse(json)
  } catch {
    throw new Error('Could not parse the file. Make sure it is valid JSON.')
  }

  if (!isObject(parsed) || parsed._type !== PLAN_SETTINGS_TYPE || !isObject(parsed.settings)) {
    throw new Error('This does not look like a plan settings export file.')
  }
  // Files exported by any older build stay importable; normalizePlanSettings
  // upgrades the shape. Only a version this build has never written is refused.
  if (!Number.isInteger(parsed._version) || parsed._version < 1 || parsed._version > PLAN_SETTINGS_VERSION) {
    throw new Error(
      `Unsupported plan settings version ${String(parsed._version)}. Expected version ${PLAN_SETTINGS_VERSION} or older.`,
    )
  }
  if (parsed._version >= 3 && parsed._version <= 5 && (!parsed.settings.planA || !parsed.settings.planB)) {
    throw new Error(`Version ${parsed._version} plan settings must include both planA and planB.`)
  }
  if (parsed._version >= 6 && !parsed.settings.planA) {
    throw new Error(`Version ${parsed._version} plan settings must include planA.`)
  }

  const settings = normalizePlanSettings(parsed.settings, parsed._version)
  for (const key of PLAN_SETTINGS_KEYS) {
    if (settings[key]) validatePlan(settings[key], `settings.${key}`)
  }
  return settings
}

export function parsePlanSettingsFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        resolve(parsePlanSettingsJson(reader.result))
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = () => reject(new Error('Failed to read the file.'))
    reader.readAsText(file)
  })
}
